import React from 'react';
import { InvoiceHire } from '../../types';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { deductionTotalFor } from '../InvoiceHire/invoiceHireListHelpers';

interface InvoiceHireHistoryRowsProps {
  invoices: InvoiceHire[];
  deductionTotals?: Map<string, number>;
  getClientName: (clientId: string) => string;
  getVesselName: (vesselId: string) => string;
}

const typeLabels: Record<string, string> = {
  invoice_hire: 'Invoice Hire',
  invoice_accommodation: 'Invoice Accommodation',
  debit_note: 'Debit',
  credit_note: 'Credit',
};

const statusClsMap: Record<string, string> = {
  paid: 'bg-green-100 text-green-800',
  sent: 'bg-blue-100 text-blue-800',
  partially_paid: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-red-100 text-red-800',
};

/**
 * All invoices (invoice-hire) appear in the client history, whatever their
 * status. Total = the backend's originalTotal; netTotal is the remaining.
 */
const InvoiceHireHistoryRows: React.FC<InvoiceHireHistoryRowsProps> = ({ invoices, deductionTotals, getClientName, getVesselName }) => {
  if (invoices.length === 0) return null;

  return (
    <>
      {invoices.map((invoice) => {
        const code = resolveCurrencyCode(invoice.currency);
        const sym = code === 'USD' ? '$' : code || '';
        const total = invoice.originalTotal ?? invoice.netTotal ?? 0;
        const netRemaining = invoice.status === 'paid' ? 0 : invoice.netTotal || 0;
        const deducted = deductionTotals ? deductionTotalFor(deductionTotals, invoice) : 0;
        const statusCls = statusClsMap[invoice.status] || 'bg-gray-100 text-gray-800';
        return (
          <tr key={`ih-${invoice.documentId || invoice.id}`} className="hover:bg-gray-50 bg-amber-50/40">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getClientName(invoice.customerId)}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.vesselId ? getVesselName(invoice.vesselId) : '-'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {invoice.invoiceNumber} — {typeLabels[invoice.type] || invoice.type}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sym} {Number(total).toLocaleString()}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">{deducted > 0 ? `- ${sym} ${deducted.toLocaleString()}` : '-'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sym} {netRemaining.toLocaleString()}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusCls}`}>{invoice.status}</span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">From Invoices</span>
            </td>
          </tr>
        );
      })}
    </>
  );
};

export default InvoiceHireHistoryRows;
